
import React, { useRef, useEffect } from "react";
import MessageBubble from "@/components/MessageBubble";
import SuggestedQuestions from "@/components/SuggestedQuestions";
import TypingIndicator from "@/components/TypingIndicator";
import { useSuggestions } from "@/hooks/useSuggestions";

interface ChatContainerProps {
  messages: any[];
  isProcessing: boolean;
  showSuggestions: boolean;
  onSuggestedQuestionClick: (question: string) => void;
  onSendMessage: (message: string) => void;
  assistantData: any;
  isDebugMode: boolean;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  isProcessing,
  showSuggestions,
  onSuggestedQuestionClick,
  onSendMessage,
  assistantData,
  isDebugMode,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  const { 
    followUpSuggestions, 
    shouldShowInitialSuggestions, 
    showFollowUpSuggestions,
    handleSuggestionClick, 
    handleInitialSuggestionClick 
  } = useSuggestions({
    onSendMessage,
    messages,
    assistantData
  });
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Function to find the last bot message that isn't an error
  const findLastBotMessageIndex = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isBot && !messages[i].error) {
        return i;
      }
    }
    return -1;
  };

  const lastBotMessageIndex = findLastBotMessageIndex();

  return (
    <div 
      ref={chatContainerRef}
      className="flex-1 overflow-y-auto p-4 scrollbar-hide bg-[#F4F1ED]"
      style={{ paddingBottom: "124px" /* 80px input + 44px buffer */ }}
    >
      {shouldShowInitialSuggestions && showSuggestions ? (
        <div className="h-full flex items-center justify-center bg-[#F4F1ED]">
          <SuggestedQuestions 
            onQuestionClick={handleInitialSuggestionClick}
            assistantData={assistantData}
          />
        </div>
      ) : (
        <div className="max-w-3xl mx-auto">
          {messages.map((message, index) => {
            // Only show suggestions for the last bot message
            const isLastBotMessage = message.isBot && 
              index === lastBotMessageIndex;
            
            return (
              <MessageBubble 
                key={message.id} 
                message={message} 
                showDebugInfo={isDebugMode} 
                onSendMessage={onSendMessage}
                suggestions={isLastBotMessage ? followUpSuggestions : []}
                showSuggestions={isLastBotMessage && showFollowUpSuggestions}
              />
            );
          })}
          {isProcessing && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  );
};

export default ChatContainer;
